import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ALLOWED_SIZES,
  CreateImageUrl,
  IMAGE_SIZE_URL_SUFFIX,
  ImageSize,
  SupportedImageTypes
} from '@simOn/common/upload-image';
import { SKIP_INSUFFICIENT_PERMISSIONS_REDIRECT } from '@simOn/common/http';
import { GenerateBlobUrlForUpload } from '@simOn/common/upload-queue/models';
import { API_URL } from '@simOn/utils/tokens';
import { Observable } from 'rxjs';
import { BlobBody } from './media.interface';

export type BlobApiEndpoint =
  | 'GenerateBlobUrlForApartmentUpload'
  | 'GenerateBlobUrlForUserUpload'
  | 'GenerateBlobUrlForNewsImageUpload';

export const DEFAULT_BLOB_API_ENDPOINT = 'GenerateBlobUrlForApartmentUpload';

@Injectable({
  providedIn: 'root'
})
export class MediaApiService {
  private readonly http = inject(HttpClient);
  private readonly _apiUrl = inject(API_URL);

  generateBlobUrlForUpload(
    { fileName, extension, size, purpose }: BlobBody,
    endpoint: BlobApiEndpoint,
    apartmentId?: string
  ) {
    let header;
    if (apartmentId) {
      header = new HttpHeaders().set('apartmentId', apartmentId);
    }
    const purposeParam = purpose ? `&Purpose=${purpose}` : '';
    return this.http.get<GenerateBlobUrlForUpload>(
      `${this._apiUrl}/Media/${endpoint}?Name=${encodeURIComponent(fileName)}&Extension=${extension}&Size=${size}${purposeParam}`,
      {
        headers: header,
        // An upload is a user action inside the apartment — a permission error must not navigate away.
        context: new HttpContext().set(SKIP_INSUFFICIENT_PERMISSIONS_REDIRECT, true)
      }
    );
  }

  generateBlobUrlForDownload(url: string): Observable<string> {
    return this.http.get(`${this._apiUrl}/Media/GenerateBlobUrlForDownload?blobUrl=${url}`, {
      responseType: 'text'
    });
  }

  generateBlobUrlForImageDownload(createImageUrl: CreateImageUrl) {
    return this.generateBlobUrlForDownload(this._getImageUrl(createImageUrl));
  }

  private _getImageUrl({ url, availableSizes, size }: CreateImageUrl) {
    if (!size || !availableSizes) return url;

    const localExtension = SupportedImageTypes.find((extension) =>
      url.includes(extension.fileExtension)
    )?.fileExtension;

    if (!localExtension) return url;

    const mappedSizes = availableSizes
      .split(',')
      .map((size) => size.trim())
      .filter((size): size is ImageSize => ALLOWED_SIZES.includes(size as ImageSize));

    const availableSize = mappedSizes.find((availableSize) => availableSize === size);

    return availableSize ? url.replace(localExtension, IMAGE_SIZE_URL_SUFFIX[availableSize] + localExtension) : url;
  }
}
